/**
	* Annotation tool view
	* Handles: annotation
	* @module AnnotationTool
*/

/// Imports

// React
import React, {useState, useEffect, useCallback} from 'react';

// OpenLayers
import OlMap from 'ol/Map';
import GeoJSON from 'ol/format/GeoJSON';
import { GeoJSONFeature } from 'ol/format/GeoJSON';
import Draw from 'ol/interaction/Draw';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Geometry from 'ol/geom/Geometry';
import Circle from 'ol/geom/Circle';
import Point from 'ol/geom/Point';
import LineString from 'ol/geom/LineString';
import { transform } from 'ol/proj';

// Tools
import { assetURL } from 'lib/utilities/asset';
import {
	removeFeatureFromSource,
	formatFeature,
	getFeatures,
	saveFeaturesRemote,
	getNextFeatureId,
	deleteFeature
} from 'lib/utilities/feature';
import { 
	getVisibleLayerCodes,
	getIsAnnotationFeatureOnMap
} from 'lib/utilities/map';
import { getToolEnabled } from 'lib/utilities/toolmodes';

// Lib
import FeatureMode from 'lib/enums/featuremode';
import MapToolMode from 'lib/enums/maptoolmode';
import OpusLayerGroup from 'lib/classes/opuslayergroup';
import OpusLayer from 'lib/classes/opuslayer';
import ChangelogMode from 'lib/enums/changelogmode';
import DrawToolInteractions from 'lib/interfaces/drawtoolinteractions';
import {
	fetchRequestJson
} from 'lib/utilities/network';

// Popups
import ColorPickerPopup from 'views/popups/ColorPicker';
import FeatureModePicker from 'views/popups/FeatureModePicker';

// Lang
import { getLocaleString } from 'lib/localization/vendor';

// Styling
import StyleAnnotationFeature from 'styles/script/styleannotationfeature';
import StyleAnnotationFeatureStreetView from 'styles/script/styleannotationfeaturestreetview';
import 'styles/css/DrawTool.scss';
import 'styles/css/AnnotationTool.scss';

interface AnnotationToolProps {
	olMap:OlMap,
	currentToolMode:MapToolMode,
}

// Interactions are set in initialize()
const Interactions:DrawToolInteractions = {}; 

/*
	Annotation source & layer for annotation features
*/
const annotationSource:VectorSource = new VectorSource();
const annotationLayer:VectorLayer<VectorSource> = new VectorLayer({
	source: annotationSource,
	zIndex: 4
});

/*
	Feature modes for annotation tool
*/
const AnnotationFeatureModes:FeatureMode[] = [
	FeatureMode.Freehand,
	FeatureMode.Polygon,
	FeatureMode.Text,
	FeatureMode.Circle,
	FeatureMode.Arrow,
	FeatureMode.StreetView
];
const FixedFeatureModes:FeatureMode[] = [
	FeatureMode.Freehand,
	FeatureMode.Polygon
]

/*
	Legend opus layer for the raster images
*/
var legendLayer:OpusLayer;
var saveTimer:any;

export default function AnnotationTool({olMap, currentToolMode}:AnnotationToolProps) {
	// Set the annotation layer to the map
	olMap.set('annotation-layer', annotationLayer);
	
	const mapConfig:any = olMap.get('opus-config');
	const format:GeoJSON = new GeoJSON({ featureProjection: mapConfig.epsgCodeView });
	
	// State hooks
	const [featureMode, setFeatureMode] = useState<FeatureMode>(FeatureMode.Freehand);
	const [penultimateFeatureMode, setPenultimateFeatureMode] = useState<FeatureMode>(FeatureMode.Text);
	const [drawnAnnotationFeatures, setDrawnAnnotationFeatures] = useState<Feature<Geometry>[]>([]);
	const [layerVisible, setLayerVisible] = useState<boolean>(false);
	const [currentColor, setCurrentColor] = useState<string>('#00ff00');
		
	if(!_config.annotationView)
		return;
	
	// Events defined from drawtool instance
	const onFeatureFormatted:Function = (f:Feature<Geometry>) => _events.invoke('featureformatted', f);
	const onFeatureSaved:Function = (f:Feature<Geometry>) => _events.invoke('featuresaved', f);
	
	// Callback hooks
	const handleKeyDownCallback = useCallback((e:KeyboardEvent) => {
		handleKeyDown(e);
	}, [featureMode, currentToolMode]);
	
	/*
		When map config changes (when locale changes) (must go first)
	*/
	useEffect(() => {
		setupLegendLayer();
	}, [mapConfig]);
	
	/*
		Initialize
	*/
	useEffect(() => {
		initialize();
	}, []);
	
	/*
		When features change
	*/
	useEffect(() => onAnnotationAddedOrRemoved(), [annotationSource.getFeatures()])
	
	/*
		Draw handlers
	*/
	useEffect(() => {
		// Events for the interactions
		const onDrawStart = (e:any) => {
			const feature:Feature<Geometry> = e.feature;
				
			feature.setId(getNextFeatureId(annotationSource));
			feature.set('Color', currentColor);
		}
		const onDrawEnd = (e:any) => {
			const feature:Feature<Geometry> = e.feature;
			
			feature.set('annotation', true); // its an annotation feature
			feature.set('origin', 'annotation');
			feature.set('Color', currentColor);
			
			formatFeature(feature, onFeatureFormatted, ChangelogMode.Created);
			setDrawnAnnotationFeatures(getFeatures(annotationSource));
			onFeatureSaved(e.feature);
			
			if(!annotationLayer.getVisible()) {
				_ui.showNotice(getLocaleString('antlayernovis'));
			}else{
				// Auto select this
				_ui.autoSelectEditFeatures([feature]);
			}
		}
		const onFreehandDrawEnd = (e:any) => {
			const feature:Feature<Geometry> = e.feature;
			const lineString:LineString = feature.getGeometry() as LineString;
			const geom:Geometry = lineString.simplify(_config.freehandSimplificationTolerance);
			
			feature.setGeometry(geom);
			
			onDrawEnd(e);
			
			feature.set('Label', '');
		}
		const onTextDrawEnd = (e:any) => {
			onDrawEnd(e);
			
			const feature:Feature<Geometry> = e.feature;
			
			feature.set('Label', getLocaleString('addtextdefault'));
			feature.set('FontSize', 16);
			feature.set('annotation', 'text');
		}
		const onArrowDrawStart = (e:any) => {
			onDrawStart(e);
			
			const feature:Feature<Geometry> = e.feature;
			
			feature.set('annotation', 'arrow');
		}
		const onArrowDrawEnd = (e:any) => {
			onDrawEnd(e);
			
			const feature:Feature<Geometry> = e.feature;
			
			feature.set('annotation', 'arrow');
			feature.set('Label', '');
		}
		const onSvDrawEnd = (e:any) => {
			onDrawEnd(e);
			
			const feature:Feature<Geometry> = e.feature;
			
			// Unset default properties
			feature.unset('Color');
			
			// Assign new sv ones
			feature.set('Notes', '');
			feature.set('annotation', 'streetview');
			
			// Auto select this
			selectStreetViewFeature(feature);
		}
		
		Interactions.Polygon.on('drawend', onDrawEnd);
		Interactions.LineString.on('drawend', onDrawEnd);
		Interactions.Circle.on('drawend', onDrawEnd);
		Interactions.Freehand.on('drawend', onFreehandDrawEnd);
		Interactions.Polygon.on('drawstart', onDrawStart);
		Interactions.LineString.on('drawstart', onDrawStart);
		Interactions.Circle.on('drawstart', onDrawStart);
		Interactions.Freehand.on('drawstart', onDrawStart);
		Interactions.Text.on('drawstart', onDrawStart);
		Interactions.Text.on('drawend', onTextDrawEnd);
		Interactions.Arrow.on('drawstart', onArrowDrawStart);
		Interactions.Arrow.on('drawend', onArrowDrawEnd);
		Interactions.StreetView.on('drawstart', onDrawStart);
		Interactions.StreetView.on('drawend', onSvDrawEnd);
		
		_config.currentAnnotationColor = currentColor;
		
		return () => {
			Interactions.Polygon.un('drawend', onDrawEnd);
			Interactions.LineString.un('drawend', onDrawEnd);
			Interactions.Circle.un('drawend', onDrawEnd);
			Interactions.Freehand.un('drawend', onFreehandDrawEnd);
			Interactions.Polygon.un('drawstart', onDrawStart);
			Interactions.LineString.un('drawstart', onDrawStart);
			Interactions.Circle.un('drawstart', onDrawStart);
			Interactions.Freehand.un('drawstart', onDrawStart);
			Interactions.Text.un('drawstart', onDrawStart);
			Interactions.Text.un('drawend', onTextDrawEnd);
			Interactions.Arrow.un('drawstart', onArrowDrawStart);
			Interactions.Arrow.un('drawend', onArrowDrawEnd);
			Interactions.StreetView.un('drawstart', onDrawStart);
			Interactions.StreetView.un('drawend', onSvDrawEnd);
		}
	}, [currentColor]); 
	
	/*
		When feature mode changes or tool mode changes
	*/
	useEffect(() => {
		updateInteractionToggle();
		
		/*
			* Capture keyboard event for callback hook
			
			* Remove it as soon as it has been handled
			
			* This is so the changing state hooks aren't captured in a closure 
			and become blind to any changes.
		*/
		window.addEventListener('keydown', handleKeyDownCallback);
		
		return () => {
			window.removeEventListener('keydown', handleKeyDownCallback);
		};
	}, [featureMode, currentToolMode]);
	
	/*
		When the current tool mode changes or features drawn
	*/
	useEffect(() => {
		// Rebuild and clear the tooltip
		_ui.tooltip.rebuild();
		_ui.tooltip.hide();
	}, [currentToolMode, drawnAnnotationFeatures]);
	
	/*
		Initialize method
	*/
	const initialize = () => {
		// Set format up
		annotationSource.set('puuid', 'annotations');
		annotationSource.set('feature-format', format);
		
		setupInteractions();
		setupMap();
		loadRemoteFeatures();
		
		// Clicked on an annotation feature
		_events.on('clicked-annotation-feature', (feature:Feature<Geometry>) => {
			_events.invoke('tool-mode-set', MapToolMode.Annotation);
		});
		
		// Saved events
		_events.on('featuresaved', (feature:Feature<Geometry>) => {
			if(!isFeatureMine(feature))
				return;
			
			syncAnnotationTool();
		});
		_events.on('remove-feature', (feature:Feature<Geometry>) => {
			if(!isFeatureMine(feature))
				return;
				
			deleteFeature(feature, annotationSource);
			syncAnnotationTool();
		});
		_events.on('delete-all-annotations', () => {
			annotationSource.getFeatures().forEach(feature => {
				deleteFeature(feature, annotationSource);
				syncAnnotationTool();
			});
		})
		_events.on('sync-annotationtool', syncAnnotationTool);
		
		// When opus layers changed visibility
		const layerUpdate = () => {
			const codes:string[] = getVisibleLayerCodes(olMap);
			const vis:boolean = codes.indexOf(_config.annotationLegendId) > -1;
			
			annotationLayer.setVisible(vis);
			setLayerVisible(vis);
		}
		
		_events.on('layer-toggled', layerUpdate);
		_events.on('layer-update', layerUpdate);
		_events.on('sync-layers', layerUpdate);
	}
	const setupMap = () => {
		olMap.addLayer(annotationLayer);
		
		olMap.addInteraction(Interactions.Polygon);
		olMap.addInteraction(Interactions.LineString);
		olMap.addInteraction(Interactions.Circle);
		olMap.addInteraction(Interactions.Freehand);
		olMap.addInteraction(Interactions.Arrow);
		olMap.addInteraction(Interactions.Text);
		olMap.addInteraction(Interactions.StreetView);
		
		annotationLayer.setStyle(StyleAnnotationFeature);
		
		// Map click static feature handler
		olMap.on('click', (e:any) => {
			if(_ui.isPointerBusy()) return;
			
			olMap.forEachFeatureAtPixel(e.pixel, (feature:Feature<Geometry>) => {
				
				// Respond when streetview annotation clicked
				if(feature.get('annotation') == 'streetview')
					selectStreetViewFeature(feature);
				
			});
		});
	}
	const setupInteractions = () => {
		// Setup
		Interactions.Polygon = new Draw({
			source: annotationSource,
			type: 'Polygon',
			style: StyleAnnotationFeature
		});
		Interactions.LineString = new Draw({
			source: annotationSource,
			type: 'LineString',
			style: StyleAnnotationFeature
		});
		Interactions.Circle = new Draw({
			source: annotationSource,
			type: 'Circle',
			style: StyleAnnotationFeature
		});
		Interactions.Freehand = new Draw({
			source: annotationSource,
			type: 'LineString',
			freehand: true,
			style: StyleAnnotationFeature
		});
		Interactions.Text = new Draw({
			source: annotationSource,
			type: 'Point',
			style: StyleAnnotationFeature
		});
		Interactions.Arrow = new Draw({
			source: annotationSource,
			type: 'LineString',
			style: StyleAnnotationFeature
		});
		Interactions.StreetView = new Draw({
			source: annotationSource,
			type: 'Point',
			style: StyleAnnotationFeatureStreetView
		}); 
	}
	
	/*
		Sync remote features
	*/
	const syncAnnotationTool = () => {
		clearTimeout(saveTimer);
		saveTimer = setTimeout(() => saveFeaturesRemote(olMap, annotationSource), 500);
	}
	
	/*
		Load annotation features from server
	*/
	const loadRemoteFeatures = () => {
		const storageId:number = _config.getStorageId();
		const prependUUID:string = annotationSource.get('puuid');
		const UUID:string = `${prependUUID}-${_config.UUID}`;
		
		const url:string = `https://${mapConfig.client}.opus4.co.uk/api/v1/call-for-sites/${storageId}/${UUID}/`;
		
		const options:any = {
			method: 'GET',
			headers: new Headers(),
			mode: 'cors',
			cache: 'default'
		}
		
		fetchRequestJson(url, options)
		.then((features:any) => {
			const featuresAdd:Array<GeoJSONFeature> = features.features.filter(
				(feature:GeoJSONFeature) => {
					const geometry:any = feature.geometry;
					const coordinates:any = geometry?.coordinates ?? [1];
					
					return coordinates.length > 0;
				}
			);
			
			// Disable the legend layer for annotations if tool isn't enabled
			if(!getToolEnabled(MapToolMode.Annotation, mapConfig))
				toggleLegendLayer(false);
			
			addFeatures(featuresAdd);
			onAnnotationAddedOrRemoved();
		})
		.catch((error:any) => {
			console.log(`Error parsing remote JSON features: ${error}`);
		});
	}
	
	/*
		Add existing features to drawing source via GeoJSONFeature array
	*/
	function addFeatures(features:Array<GeoJSONFeature>) {
		for(let geoJsonFeature of features) {
			let feature:Feature<Geometry> = format.readFeature(geoJsonFeature);
			
			feature.setId(getNextFeatureId(annotationSource));
			
			formatFeature(feature, onFeatureFormatted);
			
			annotationSource.addFeature(feature);
		}
	}
	
	/*
		Abort drawing in progress
	*/
	const abortDrawing = () => {
		switch(featureMode) {
			case FeatureMode.Polygon:
				Interactions.Polygon.abortDrawing();
			break;
			case FeatureMode.LineString:
				Interactions.LineString.abortDrawing();
			break;
			case FeatureMode.Circle:
				Interactions.Circle.abortDrawing();
			break;
			case FeatureMode.Freehand:
				Interactions.Freehand.abortDrawing();
			break;
		}
	}
	
	/*
		Toggle interactions based on drawing mode and the active map tool
	*/
	const updateInteractionToggle = () => {
		Interactions.Polygon.setActive(
			featureMode == FeatureMode.Polygon &&
			currentToolMode == MapToolMode.Annotation
		);
		Interactions.LineString.setActive(
			featureMode == FeatureMode.LineString &&
			currentToolMode == MapToolMode.Annotation
		);
		Interactions.Circle.setActive(
			featureMode == FeatureMode.Circle &&
			currentToolMode == MapToolMode.Annotation
		);
		Interactions.Freehand.setActive(
			featureMode == FeatureMode.Freehand &&
			currentToolMode == MapToolMode.Annotation
		);
		Interactions.Text.setActive(
			featureMode == FeatureMode.Text &&
			currentToolMode == MapToolMode.Annotation
		);
		Interactions.Arrow.setActive(
			featureMode == FeatureMode.Arrow &&
			currentToolMode == MapToolMode.Annotation
		);
		Interactions.StreetView.setActive(
			featureMode == FeatureMode.StreetView &&
			currentToolMode == MapToolMode.Annotation
		);
	}
	
	/*
		Update feature mode
	*/
	const updateFeatureMode = (mode:FeatureMode) => {
		setFeatureMode(mode);
	}
	
	/*
		On annotation added or removed
	*/
	const onAnnotationAddedOrRemoved = () => {
		const isAnnotationOnMap:boolean = getIsAnnotationFeatureOnMap(olMap);
		
		// Show/hide legend layer group if present or not
		toggleLegendLayerListing(isAnnotationOnMap && getToolEnabled(MapToolMode.Annotation, mapConfig));
	}
	
	/*
		Handle key down
	*/
	const handleKeyDown = (e:KeyboardEvent) => {
		// Something like an input box is selected so abort this handler
		if(_ui.hasFocus()) return;
		
		if(e.key == 'Escape') {
			if(showAnnotationTool()) {
				e.preventDefault();
				abortDrawing();
			}
		}
	}
	
	/*
		Is this view meant to be active?
	*/
	const showAnnotationTool = ():boolean => {
		return (currentToolMode == MapToolMode.Annotation || currentToolMode == MapToolMode.Select || currentToolMode == MapToolMode.MapLayers) && layerVisible;
	}
	/*
		Clear the drawn features
	*/
	const clearDrawnAnnotationFeatures = () => {
		annotationSource.clear();
		
		setDrawnAnnotationFeatures(getFeatures(annotationSource));
	}
	/*
		Click clear the drawn features
	*/
	const clickClearAnnotationmentFeatures = () => {
		_ui.showConfirm(
			getLocaleString('clearconfirm', [getFeatures(annotationSource).length.toString()]), 
			() => clearDrawnAnnotationFeatures()
		);
	}
	const selectStreetViewFeature = (feature:Feature<Geometry>) => {
		const point:Point = feature.getGeometry() as Point;
		const coords:number[] = transform(
			point.getCoordinates(),
			mapConfig.epsgCodeView,
			"EPSG:4326" // lat long
		);
		
		const lng:number = coords[0];
		const lat:number = coords[1];
		
		_ui.openGoogleMapViewAt(lng, lat, '', {feature: feature});
	}
	const toggleLegendLayer = (visible:boolean) => {
		_events.invoke('toggle-layer-code', _config.annotationLegendId, visible);
	}
	const toggleLegendLayerListing = (vis:boolean) => {
		_events.invoke('hide-layer', legendLayer, !vis);
	}
	const setupLegendLayer = () => {
		const layerGroups:OpusLayerGroup[] = olMap.get('opus-layer-groups');
		
		legendLayer = null;
		
		layerGroups.forEach((layerGroup:OpusLayerGroup) => {
			layerGroup.layers.forEach((layer:OpusLayer) => {
				if(layer.id == _config.annotationLegendId)
					legendLayer = layer;
			})
		});
		
		if(!legendLayer)
			throw new Error('No annotation legend layer found. There must be one.');
	}
	
	/**
		* View draw methods
	*/
	const drawFixedFeatureToolModeButtons = () => {
		const rows:any[] = [];
		
		FixedFeatureModes.forEach(m => rows.push(drawFeatureModeToolButton(m)));
		
		return rows;
	}
	const drawFeatureModeToolButton = (mode:FeatureMode, penultimate:boolean = false) => {
		const name:string = FeatureMode[mode];
		const className:string = `drawing-option drawing-option--${name.toLowerCase()} ${penultimate ? 'annotation-option--penultimate' : ''} annotation-option annotation-option--${name.toLowerCase()} ${featureMode == mode ? ` drawing-option--selected ${_ui.getSelClassName()} drawing-option--selected--${name.toLowerCase()}` : ''}`;
		
		ColorPickerPopup.set('color', currentColor);
		ColorPickerPopup.set('onSave', (hex:string) => setCurrentColor(hex));
		
		const shouldDrawBlob = (mode:FeatureMode) => {
			switch(mode) {
				case FeatureMode.StreetView:
					return false;
				default:
					return true;
			}
		}
		const drawBlob = () => {
			return (
				<div className='annotation-color-blob' style={{backgroundColor: currentColor}} onClick={() => _ui.showPopup(ColorPickerPopup)}></div>
			)
		}
		
		return (
			<div className={className} onClick={() => updateFeatureMode(mode)} data-tip={getLocaleString(name.toLowerCase())} key={mode}>
				<img src={assetURL(`assets/images/${name.toLowerCase()}.png`)} />
				
				{featureMode == mode && shouldDrawBlob(mode) && drawBlob()}
			</div>
		)
	}
	const drawMoreFeatureModesButton = () => {
		const className:string = `drawing-option drawing-option--more annotation-option annotation-option--more`;
		
		FeatureModePicker.set('annotation-layer', annotationLayer);
		FeatureModePicker.set('feature-modes', AnnotationFeatureModes);
		FeatureModePicker.set('feature-mode-select', (featureMode:FeatureMode) => {
			!FixedFeatureModes.includes(featureMode) && setPenultimateFeatureMode(featureMode);
			updateFeatureMode(featureMode)
		});
		
		return (
			<div className={className} onClick={() => _ui.showPopup(FeatureModePicker)} data-tip={getLocaleString('moretool')}>
				<img src={assetURL(`assets/images/more.png`)} />
			</div>
		)
	}
	const drawAnnotationTool = () => {
		return (
			<>
				{drawFixedFeatureToolModeButtons()}
				{drawFeatureModeToolButton(penultimateFeatureMode, true)}
				{drawMoreFeatureModesButton()}
			</>
		)
	}
	
	return (
		<>
			{currentToolMode == MapToolMode.Annotation && drawAnnotationTool()}
		</>
	)
}

function isFeatureMine(feature:Feature<Geometry>) {
	switch(feature.get('origin')) {
		case 'annotation':
			return true;
		default:
			return false;
	}
}